import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  List,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { API_BASE_URL } from '../api/client.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';

/**
 * MCP — what the AI can do, and how to point it here.
 *
 * User-scoped, so it lives in the header cluster and not under Indstillinger.
 * That is not cosmetic: the access token is per-user by ADR-007, and a page
 * filed beneath a heading named after the active company would imply the token
 * changes when the switcher moves. It does not.
 *
 * **This page generates no tokens.** They belong with the identity they belong
 * to, which is the user, so the Setup tab links to Min konto instead. That was
 * decided before this page was built and is worth not quietly undoing: putting
 * a "create token" button here is the obvious convenience, and it would put the
 * per-user credential on a page people reach while thinking about one company.
 */

/**
 * Where an AI client connects.
 *
 * Derived from the API's own origin rather than written down, because those two
 * being the same host is the deployment's whole arrangement — one hostname,
 * split by path prefix, so the session cookie works and there is no CORS. A
 * hard-coded address would be wrong the first time anyone ran this anywhere
 * else, and the instructions would look right while failing.
 *
 * In development `VITE_API_BASE_URL` points at the API on :3001 and the MCP
 * server is a separate process on :3002, so the derived URL is wrong locally
 * and right in production. The tab says so rather than pretending otherwise.
 */
function mcpUrl(): string {
  try {
    return new URL('/mcp', API_BASE_URL).toString();
  } catch {
    return '/mcp';
  }
}

interface ToolGroup {
  readonly area: MessageKey;
  readonly reads: MessageKey;
  readonly writes: MessageKey;
}

/**
 * What the tools cover, by area rather than by name.
 *
 * Forty-three names is a list nobody reads and a list that goes stale on the
 * next commit. The areas are what a person needs in order to decide whether to
 * connect an AI at all.
 */
const TOOL_GROUPS: readonly ToolGroup[] = [
  {
    area: 'mcp.group.bookkeeping',
    reads: 'mcp.group.bookkeeping.reads',
    writes: 'mcp.group.bookkeeping.writes',
  },
  {
    area: 'mcp.group.attachments',
    reads: 'mcp.group.attachments.reads',
    writes: 'mcp.group.attachments.writes',
  },
  {
    area: 'mcp.group.bank',
    reads: 'mcp.group.bank.reads',
    writes: 'mcp.group.bank.writes',
  },
  {
    area: 'mcp.group.invoices',
    reads: 'mcp.group.invoices.reads',
    writes: 'mcp.group.invoices.writes',
  },
  {
    area: 'mcp.group.vat',
    reads: 'mcp.group.vat.reads',
    writes: 'mcp.group.vat.writes',
  },
  {
    area: 'mcp.group.reports',
    reads: 'mcp.group.reports.reads',
    writes: 'mcp.group.reports.writes',
  },
  {
    area: 'mcp.group.inbound',
    reads: 'mcp.group.inbound.reads',
    writes: 'mcp.group.inbound.writes',
  },
  {
    area: 'mcp.group.company',
    reads: 'mcp.group.company.reads',
    writes: 'mcp.group.company.writes',
  },
];

function GeneralTab(): ReactNode {
  const t = useT();

  return (
    <Stack gap="lg">
      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={500}>{t('mcp.whatTitle')}</Text>
          <Text size="sm">{t('mcp.what1')}</Text>
          <Text size="sm">{t('mcp.what2')}</Text>
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={500}>{t('mcp.asymmetryTitle')}</Text>
          {/*
            Split across five keys so the two bold runs survive translation.
            One key with markup in it would put HTML in a message catalogue,
            which is how a translator ends up breaking a tag.
          */}
          <Text size="sm">
            {t('mcp.asymmetry1a')}
            <strong>{t('mcp.asymmetry1b')}</strong>
            {t('mcp.asymmetry1c')}
            <strong>{t('mcp.asymmetry1d')}</strong>
            {t('mcp.asymmetry1e')}
          </Text>
          <Text size="sm">{t('mcp.asymmetry2')}</Text>
          <Text size="sm">{t('mcp.asymmetry3')}</Text>
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={500}>{t('mcp.canTitle')}</Text>
          <Table.ScrollContainer minWidth={560}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('mcp.col.area')}</Table.Th>
                  <Table.Th>{t('mcp.col.reads')}</Table.Th>
                  <Table.Th>{t('mcp.col.writes')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {TOOL_GROUPS.map((group) => (
                  <Table.Tr key={group.area}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {t(group.area)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{t(group.reads)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{t(group.writes)}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={500}>{t('mcp.cannotTitle')}</Text>
          <List size="sm" spacing="xs">
            <List.Item>{t('mcp.cannot.delete')}</List.Item>
            <List.Item>{t('mcp.cannot.otherCompany')}</List.Item>
            <List.Item>{t('mcp.cannot.role')}</List.Item>
            <List.Item>{t('mcp.cannot.silent')}</List.Item>
          </List>
        </Stack>
      </Card>
    </Stack>
  );
}

function SetupTab(): ReactNode {
  const t = useT();
  const url = mcpUrl();
  const config = `{
  "mcpServers": {
    "regnskab": {
      "type": "http",
      "url": "${url}",
      "headers": {
        "Authorization": "Bearer DIN_NØGLE_HER"
      }
    }
  }
}`;

  return (
    <Stack gap="lg">
      {/*
        Two ways in, and the order is not a preference. Browser login is the
        only one that works in the Claude desktop and web apps: a connector
        there is a URL and nothing else, with no field for a token and no way to
        send a header. The pasted key is for clients that read a configuration
        file, which is Claude Code and the editors. See ADR-008.
      */}
      <Card withBorder padding="md">
        <Stack gap="sm">
          <Group gap="xs">
            <Text fw={500}>{t('mcp.setup.browser.title')}</Text>
            <Badge variant="light">{t('mcp.setup.browser.badge')}</Badge>
          </Group>
          <Text size="sm">{t('mcp.setup.browser.body')}</Text>
          <Group gap="xs" wrap="nowrap">
            <Code style={{ wordBreak: 'break-all' }}>{url}</Code>
            <CopyButton value={url}>
              {({ copied, copy }) => (
                <Button size="xs" variant="default" onClick={copy}>
                  {copied ? t('common.copied') : t('common.copy')}
                </Button>
              )}
            </CopyButton>
          </Group>
          <List size="sm" spacing="xs" type="ordered">
            <List.Item>{t('mcp.setup.browser.step1')}</List.Item>
            <List.Item>{t('mcp.setup.browser.step2')}</List.Item>
            <List.Item>{t('mcp.setup.browser.step3')}</List.Item>
          </List>
          <Text size="sm" c="dimmed">
            {t('mcp.setup.browser.noKey')}
          </Text>
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={500}>{t('mcp.setup.file.title')}</Text>
          <Text size="sm">{t('mcp.setup.file.body')}</Text>
          <Text size="sm">
            {t('mcp.setup.step1a')}
            <Anchor component={Link} to="/min-konto">
              {t('nav.myAccount')}
            </Anchor>
            {t('mcp.setup.step1b')}
          </Text>
          <Text size="sm" c="dimmed">
            {t('mcp.setup.onceOnly')}
          </Text>
          <Text size="sm">
            {t('mcp.setup.step3a')}
            <Code>{t('mcp.setup.keyPlaceholder')}</Code>
            {t('mcp.setup.step3b')}
          </Text>
          <Code block style={{ whiteSpace: 'pre' }}>
            {config}
          </Code>
          <Group>
            <CopyButton value={config}>
              {({ copied, copy }) => (
                <Button size="xs" variant="default" onClick={copy}>
                  {copied ? t('common.copied') : t('mcp.setup.copyConfig')}
                </Button>
              )}
            </CopyButton>
            <Button component={Link} to="/min-konto" variant="default" size="xs">
              {t('mcp.setup.goToAccount')}
            </Button>
          </Group>
          <Text size="sm" c="dimmed">
            {t('mcp.setup.file.company')}
          </Text>
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={500}>{t('mcp.setup.step4')}</Text>
          <Text size="sm">{t('mcp.setup.try')}</Text>
          <Text size="sm" c="dimmed">
            {t('mcp.setup.overHttp')}
          </Text>
        </Stack>
      </Card>

      <Alert color="blue" variant="light" title={t('mcp.setup.noExpiryTitle')}>
        {t('mcp.setup.noExpiry')}
      </Alert>
    </Stack>
  );
}

export function McpPage(): ReactNode {
  const t = useT();

  return (
    <Stack gap="lg">
      <Title order={2}>{t('mcp.title')}</Title>

      <Text c="dimmed">{t('mcp.intro')}</Text>

      <Tabs defaultValue="general">
        <Tabs.List mb="md">
          <Tabs.Tab value="general">{t('mcp.tab.general')}</Tabs.Tab>
          <Tabs.Tab value="setup">{t('mcp.tab.setup')}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="general">
          <GeneralTab />
        </Tabs.Panel>

        <Tabs.Panel value="setup">
          <SetupTab />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
